import React, { useState, useEffect, useCallback } from "react";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import FeatherIcon from "feather-icons-react";
import { Link } from "react-router-dom";
import { fetchAllEventsWithUsers } from "../../services/eventService";
import { message } from "antd";
import { BASE_URL_IMAGE } from "../../config/api";
import FormattedContentForCards from "../../components/Common/FormattedContentForCards";
import "./EventCard.css";

const EventView = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  const [userImageErrors, setUserImageErrors] = useState({});

  const fetchEvents = useCallback(async () => {
    try {
      const data = await fetchAllEventsWithUsers();
      const validEvents = data.filter((event) => event.user_id && event.user_id !== 0);
      setEvents(validEvents);
    } catch (error) {
      console.error("Error fetching events:", error);
      message.error("Failed to load events.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleEventImageError = (eventId) => {
    setImageLoadErrors((prev) => ({ ...prev, [eventId]: true }));
  };

  const handleUserImageError = (userId) => {
    setUserImageErrors((prev) => ({ ...prev, [userId]: true }));
  };

  const formatDate = (date) => {
    if (!date) return "No Date";
    return new Date(date).toLocaleDateString("en-GB").replace(/\//g, "-");
  };

  const getDuration = (start, end) => {
    if (!start || !end) return "Unknown";
    const diff = (new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24);
    return diff <= 0 ? "1-Day Event" : `${diff + 1}-Day Event`;
  };

  const renderEventCard = (event) => {
    const eventImg = imageLoadErrors[event.event_id]
      ? "/assets/img/blog/blog-01.jpg"
      : `${BASE_URL_IMAGE}/uploads/eventImages/${event.event_img}`;
    const userImg = userImageErrors[event.user_id]
      ? "/assets/img/profiles/avatar-12.jpg"
      : `${BASE_URL_IMAGE}/uploads/userProfiles/${event.userImage}`;

    return (
      <div className="col-sm-6 col-md-6 col-xl-4 mb-4" key={event.event_id}>
        <div className={`evcard ${event.event_isFeatured ? "evcard-active" : "evcard-inactive"}`}>
          <div className="evcard-img-wrapper">
            <img
              src={eventImg}
              alt={event.event_title}
              className="evcard-img"
              onError={() => handleEventImageError(event.event_id)}
            />
            <div className="evcard-badges">
              <span className={`evcard-badge ${event.event_isFeatured ? "bg-success" : "bg-secondary"}`}>
                {event.event_isFeatured ? "ACTIVE" : "INACTIVE"}
              </span>
              <span className="evcard-badge bg-primary">
                {getDuration(event.event_startDate, event.event_endDate).toUpperCase()}
              </span>
            </div>
          </div>

          <div className="evcard-content">
            <div className="evcard-author">
              <img
                src={userImg}
                alt="author"
                className="evcard-author-img"
                onError={() => handleUserImageError(event.user_id)}
              />
              <div className="ms-2">
                <h5 className="mb-0 fw-bold">{event.userName || "Unknown"}</h5>
                <small className="text-muted">{event.userDesignation || "No Role"}</small>
              </div>
              <div className="ms-auto text-muted d-flex align-items-center">
                <FeatherIcon icon="calendar" size={14} className="me-1" />
                {formatDate(event.event_startDate)}
              </div>
            </div>

            <h5 className="fw-bold mt-3 mb-2">{event.event_title}</h5>
            <p className="evcard-desc">
              <FormattedContentForCards htmlContent={event.event_content} />
            </p>

            <div className="evcard-footer">
              <div>
                <div className="fw-semibold">{event.event_conductedBy}</div>
                <div className="text-muted">{event.event_mode}</div>
              </div>
              <div className="d-flex gap-2">
                <Link to={`/event-detail/${event.event_id}`} className="btn btn-sm btn-view">
                  View
                </Link>
                <Link to={`/edit-event/${event.event_id}`} className="btn btn-sm btn-edit">
                  Edit
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const activeEvents = events.filter((e) => e.event_isFeatured === 1);
  const inactiveEvents = events.filter((e) => e.event_isFeatured !== 1);

  return (
    <div className="main-wrapper">
      <Header />
      <Sidebar id="menu-item11" id1="menu-items11" activeClassName="blog-grid" />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/list-events">Event</Link>
                  </li>
                  <li className="breadcrumb-item active">Events</li>
                </ul>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="row">
              {activeEvents.length > 0 && (
                <div className="col-12 mb-4">
                  <h4 className="text-success fw-bold mb-3">
                    <FeatherIcon icon="check-circle" className="me-2" />
                    Active Events ({activeEvents.length})
                  </h4>
                  <div className="row">
                    {activeEvents.map(renderEventCard)}
                  </div>
                </div>
              )}

              {inactiveEvents.length > 0 && (
                <div className="col-12 mt-5">
                  <h4 className="text-muted fw-bold mb-3">
                    <FeatherIcon icon="slash" className="me-2" />
                    Inactive Events ({inactiveEvents.length})
                  </h4>
                  <div className="row">
                    {inactiveEvents.map(renderEventCard)}
                  </div>
                </div>
              )}

              {events.length === 0 && (
                <div className="text-center py-5">
                  <h5>No events available.</h5>
                  <Link to="/add-event" className="btn btn-primary mt-2">Create New Event</Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventView;
